/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useRef, useState } from "react";

import Link from "next/link";
import { Transition, TransitionStatus } from "react-transition-group";
import styled, { css } from "styled-components";

import { Layer } from "@vienna/common";

import { useCurrentUser } from "src/app/useCurrentUser";
import { InviteFriends } from "src/components/Invites/InviteFriends";
import { Avatar } from "src/components/Shared/User/Avatar";
import { Username } from "src/components/Shared/User/Username";
import { StyledLink } from "src/components/UI/Links/Links";
import { Tooltip } from "src/components/UI/Tooltip";
import { AppRoutes } from "src/lib/constants";
import { useOnMouseDownOutside } from "src/lib/hooks/useOnMouseDownOutside";
import { safeSegmentTrack } from "src/lib/logging/segment";

interface Props {
  rendered: boolean;
  closeDropdown: () => void;
  avatarRef: React.RefObject<HTMLDivElement>;
}
const FEEDBACK_URL = "https://forms.gle/UQgyS4VuxswdYdHv6";

export function UserDropdown({ rendered, closeDropdown, avatarRef }: Props) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [inviteActive, setInviteActive] = useState(false);

  const currentUser = useCurrentUser()!;
  const remainingInvites = currentUser.observe((state) => state.remainingInvites);
  const hasInvites = remainingInvites > 0;
  const unreadNotificationCount = currentUser.observe((state) => state.unreadNotificationCount);

  const handleClickOutsideClose = (e: MouseEvent) => {
    // if you clicked the avatar, don't fire. Menu will close in the onClick handler in the Avatar component
    if (avatarRef.current?.contains(e.target as Node)) {
      return;
    }
    closeDropdown();
  };

  useOnMouseDownOutside(dropdownRef, (e) => handleClickOutsideClose(e));

  const handleInvite = () => {
    if (hasInvites) {
      setInviteActive(true);
      safeSegmentTrack({ event: "Invite Modal Open" });
    }
  };

  return (
    <>
      {inviteActive && hasInvites && <InviteFriends setInviteActive={setInviteActive} />}
      <Transition in={rendered} timeout={50} mountonEnter appear unmountOnExit>
        {(status) => (
          <StyledDropdown status={status} ref={dropdownRef}>
            <StyledUser>
              <Link passHref href={currentUser.inAppLink}>
                <StyledLink onClick={() => closeDropdown()}>
                  <Avatar src={currentUser.avatarURL(33)} size={33} />
                </StyledLink>
              </Link>
              <StyledNames>
                <div onClick={() => closeDropdown()}>
                  <Username user={currentUser} />
                </div>
                <Link passHref href={currentUser.inAppLink}>
                  <StyledLink>
                    <StyledDisplayName onClick={() => closeDropdown()}>
                      {currentUser.snapshot.displayName}
                    </StyledDisplayName>
                  </StyledLink>
                </Link>
              </StyledNames>
            </StyledUser>
            <Actions>
              <Link passHref href={currentUser.inAppLink}>
                <StyledActionLink onClick={() => closeDropdown()}>PROFILE</StyledActionLink>
              </Link>
              <Tooltip tooltipText={hasInvites ? "Invite Users" : "We'll be enabling invites soon!"}>
                <StyledActionText $disabled={!hasInvites} onClick={() => handleInvite()} as="div">
                  INVITE
                  {hasInvites && <StyledNotificationCount> [{remainingInvites}]</StyledNotificationCount>}
                </StyledActionText>
              </Tooltip>
              <Link passHref href="/notifications">
                <StyledActionLink href="/notifications" onClick={() => closeDropdown()}>
                  <span>
                    NOTIFICATIONS
                    {unreadNotificationCount > 0 && (
                      <StyledNotificationCount> [{unreadNotificationCount}]</StyledNotificationCount>
                    )}
                  </span>
                </StyledActionLink>
              </Link>
              <Link passHref href="/settings">
                <StyledActionLink onClick={() => closeDropdown()}>SETTINGS</StyledActionLink>
              </Link>
              <Link passHref href={AppRoutes.Logout}>
                <StyledActionLink onClick={() => closeDropdown()}>LOG_OUT</StyledActionLink>
              </Link>
              <Divider />
              <Link passHref href="/meta">
                <StyledActionLink onClick={() => closeDropdown()} target="_blank" style={{ paddingTop: "0px" }}>
                  <span>ABOUT VIENNA</span>
                </StyledActionLink>
              </Link>
              <Link passHref href={FEEDBACK_URL}>
                <StyledActionLink onClick={() => closeDropdown()} target="_blank">
                  <span>REPORT A BUG</span>
                </StyledActionLink>
              </Link>
            </Actions>
          </StyledDropdown>
        )}
      </Transition>
    </>
  );
}

interface ViewsDropdownProps {
  rendered: boolean;
  closeDropdown: () => void;
  viewsRef: React.RefObject<HTMLDivElement>;
}

// a menu you can open on plate that has links to home, explore, and feed
export function ViewsDropdown({ rendered, closeDropdown, viewsRef }: ViewsDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutsideClose = (e: MouseEvent) => {
    // if you clicked the avatar, don't fire. Menu will close in the onClick handler in the Avatar component
    if (viewsRef.current?.contains(e.target as Node)) {
      return;
    }
    closeDropdown();
  };

  useOnMouseDownOutside(dropdownRef, (e) => handleClickOutsideClose(e));

  return (
    <Transition in={rendered} timeout={50} mountonEnter appear unmountOnExit>
      {(status) => (
        <StyledViewsDropdown status={status} ref={dropdownRef}>
          <Link passHref href="/">
            <StyledActionLink style={{ paddingTop: "0px" }} onClick={() => closeDropdown()}>
              HOME
            </StyledActionLink>
          </Link>
          <Link passHref href="/explore">
            <StyledActionLink onClick={() => closeDropdown()}>EXPLORE</StyledActionLink>
          </Link>
        </StyledViewsDropdown>
      )}
    </Transition>
  );
}

const StyledDropdown = styled.div<{ status: TransitionStatus }>`
  position: absolute;
  top: 55px;
  right: 30px;
  padding: 15px;
  min-width: 220px;
  border: ${({ theme }) => `2px solid ${theme.primary}`};
  background-color: ${({ theme }) => theme.background};
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.text.default};
  font-weight: 500;
  font-size: 12px;
  font-family: "IBM Plex Mono";
  transition: all 50ms; //should match transition timeout
  opacity: ${({ status }) => {
    switch (status) {
      case "entering":
        return 0;
      case "entered":
        return 1;
      case "exiting":
        return 0;
      case "exited":
        return 0;
      default:
        return 0;
    }
  }};
`;

const StyledViewsDropdown = styled(StyledDropdown)`
  position: absolute;
  top: 25px;
  right: unset;
  min-width: 120px;
  z-index: ${Layer.WIN_LAYER};
`;

const StyledUser = styled.div`
  display: flex;
`;

const StyledNames = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-left: 11px;
`;

const StyledDisplayName = styled.div`
  color: ${({ theme }) => theme.text.subtle};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 15px;
  flex-grow: 0;
`;

const StyledActionLink = styled.a`
  text-decoration: none;
  color: inherit;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 700;
  padding-top: 5px;
  &:hover {
    color: ${({ theme }) => theme.text.default};
    text-decoration: underline;
  }
`;

const StyledActionText = styled.div<{ $disabled?: boolean }>`
  text-decoration: none;
  color: inherit;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 700;
  padding-top: 5px;
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.text.default};
    text-decoration: underline;
  }
  ${({ $disabled }) =>
    $disabled &&
    css`
      color: ${({ theme }) => theme.text.disabled};
      &:hover {
        color: ${({ theme }) => theme.text.disabled};
        text-decoration: none;
      }
    `}
`;

const StyledNotificationCount = styled.span`
  color: ${({ theme }) => theme.text.subtle};
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${({ theme }) => theme.text.subtle};
  margin: 12px 0px;
`;
